// CaptureErrorFallback — route `errorElement` for the /capture subtree.
//
// React Router renders this in place of a capture screen when that
// screen (or any descendant) throws during render. Before this, the
// capture tree had no error boundary — only a Suspense fallback in
// router.tsx — so a single render throw unmounted the whole subtree and
// left the surveyor on a blank screen with no way back. Field staff
// reported "components go blank after applying a quantity value"; this
// turns that dead-end into a recoverable surface (reload / back to start)
// and records the throw to Sentry so the underlying cause is diagnosable.
//
// Deliberately self-contained: no CaptureLayout / user-menu / breadcrumb
// chrome, no data hooks. An error fallback that depends on the same
// surfaces that just failed can throw again and defeat the boundary, so
// this renders only plain markup + a lucide icon. The one module import
// it does take — `flushAllCaptureClients` (#810/F4 below) — is a pure
// persist-drain over the client registry, not a data hook, and is called
// only from the Reload handler (never render), so it cannot reintroduce
// the throw-again risk.

import { useCallback, useEffect, useState, type ReactElement } from 'react'

import { AlertTriangle } from 'lucide-react'
import { useRouteError } from 'react-router'
import * as Sentry from '@sentry/react'

import { flushAllCaptureClients } from '../client/capture-client-registry'

// Upper bound on the pre-reload coalesce drain. Mirrors auth-provider's
// HELD_FLUSH_TIMEOUT_MS (#726 logout). A healthy flush is sub-second; the bound
// only trips on a wedged write — on which we FAIL CLOSED to the retry affordance
// rather than leaving Reload stuck on "Saving…" forever (#810 P3).
const DEFAULT_FLUSH_TIMEOUT_MS = 3_000

/**
 * `reload` and `flushTimeoutMs` are injectable so tests can assert the
 * flush-before-reload ordering (without redefining `window.location` — jsdom
 * can't `reload()`) and the timeout fail-closed without a real 3 s wait. The
 * router renders `<CaptureErrorFallback />` with no props, so production uses the
 * defaults. Mirrors `registerChunkReloadOnPreloadError` in service-worker-updates.ts.
 */
export function CaptureErrorFallback({
  reload = () => {
    window.location.reload()
  },
  flushTimeoutMs = DEFAULT_FLUSH_TIMEOUT_MS,
}: { reload?: () => void; flushTimeoutMs?: number } = {}): ReactElement {
  const error = useRouteError()
  const [reloadState, setReloadState] = useState<'idle' | 'saving' | 'error'>(
    'idle',
  )

  useEffect(() => {
    // The capture subtree handles contractual asset data, so a render
    // throw must be diagnosable. Record once when the boundary catches.
    Sentry.captureException(error, { extra: { boundary: 'capture' } })
  }, [error])

  // #810/F4: `window.location.reload()` destroys the document — and with it any
  // +/- tap still parked in the capture client's 500 ms in-memory coalesce
  // before it has been written to local SQLite / ps_crud. The reassurance copy
  // below ("your saved counts are safe") was therefore a LIE for that window:
  // Reload silently dropped the in-flight count. So drain every registered
  // capture client's coalesce to durable storage FIRST, and FAIL CLOSED — if a
  // persist fails, do NOT reload (reloading would lose the unpersisted tap):
  // keep the document alive so the tap stays held in the live coalesce, and
  // surface a retry. `flushAllCaptureClients()` itself fails closed (a failed
  // persist rejects rather than being swallowed). This composes with the
  // flush-on-unmount fix (#817): when the throwing screen unmounts it already
  // best-effort-flushes its coalesce, so this awaited drain is the belt that
  // covers any client still registered when the fallback renders.
  const handleReload = useCallback(async (): Promise<void> => {
    setReloadState('saving')
    let timer: ReturnType<typeof setTimeout> | undefined
    try {
      // Bound the drain (#810 P3): a flush that never settles (wedged SQLite /
      // SharedWorker) must not leave Reload stuck on "Saving…" forever. On
      // timeout we reject into the catch below and fail closed, exactly like the
      // logout flow's flushHeldWork (auth-provider.tsx).
      await Promise.race([
        flushAllCaptureClients(),
        new Promise<never>((_resolve, reject) => {
          timer = setTimeout(
            () => reject(new Error('capture-flush-timeout')),
            flushTimeoutMs,
          )
        }),
      ])
    } catch {
      // A failed persist OR a wedged flush past the bound — do NOT reload (it
      // would destroy a still-unpersisted tap). Keep the document alive (the tap
      // stays held / retrying in the live coalesce) and surface the retry.
      setReloadState('error')
      return
    } finally {
      if (timer !== undefined) clearTimeout(timer)
    }
    reload()
  }, [reload, flushTimeoutMs])

  return (
    <div
      role="alert"
      className="mx-auto flex min-h-screen w-full max-w-md flex-col items-center justify-center gap-4 bg-[color:var(--color-cc-cream)] p-6 text-center text-[color:var(--color-cc-charcoal)]"
    >
      <AlertTriangle
        className="h-10 w-10 text-[color:var(--color-cc-orange)]"
        aria-hidden="true"
      />
      <h1 className="font-display text-lg font-semibold">
        Something went wrong on this screen
      </h1>
      <p className="text-sm text-[color:var(--color-cc-charcoal)]/70">
        Your saved counts are safe on this device. Reload to keep capturing —
        if it keeps happening, head back to the start and re-open the room.
      </p>
      {reloadState === 'error' ? (
        <p
          role="status"
          className="text-sm font-medium text-[color:var(--color-cc-orange)]"
        >
          We couldn&apos;t finish saving your latest count. It&apos;s still held
          on this device — tap Reload to try again.
        </p>
      ) : null}
      <div className="flex flex-wrap items-center justify-center gap-3 pt-2">
        <button
          type="button"
          onClick={() => {
            void handleReload()
          }}
          disabled={reloadState === 'saving'}
          className="inline-flex min-h-12 min-w-11 items-center justify-center rounded-2xl bg-[color:var(--color-cc-forest)] px-6 text-sm font-semibold text-[color:var(--color-cc-cream)] hover:bg-[color:var(--color-cc-teal)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--color-cc-lime)] disabled:cursor-not-allowed disabled:opacity-60"
        >
          {reloadState === 'saving' ? 'Saving…' : 'Reload'}
        </button>
        <a
          href="/capture"
          className="inline-flex min-h-12 items-center px-4 text-sm font-semibold text-[color:var(--color-cc-forest)] hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--color-cc-lime)]"
        >
          Back to start
        </a>
      </div>
    </div>
  )
}
